import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { ServerTypes } from 'src/app/models/servers';
import { ReAssignWorkOrderCommand, WorkOrder } from 'src/app/interfaces/workorder';
import { WorkitemPreloadService } from './workitem-preload.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { StorageService } from './storage.service';
import { CoreStorageEnum } from '../models/core-storage.enum';
import { Router } from '@angular/router';

export interface WorkOrderAction {
  action: any;
  timeMs: number;
}

@Injectable()
export class WorkOrderService {
  private workOrdersSubject = new BehaviorSubject<WorkOrder[]>(null);
  private nextWorkOrder = new Subject<WorkOrder>();
  private currentWorkItemIdSubject = new BehaviorSubject<string | null>(null);
  private workOrders: WorkOrder[] = [];

  // Server adress
  private readonly serverUrl: string;

  // Endpoints
  private readonly workorderUrl = "v1/workitems/";

  public workOrders$: Observable<WorkOrder[]> = this.workOrdersSubject.asObservable();
  public nextWorkOrder$: Observable<WorkOrder> = this.nextWorkOrder.asObservable();
  public currentWorkItemId: Observable<string> = this.currentWorkItemIdSubject.asObservable();

  constructor(
    private http: HttpClient, 
    private apiService: ApiService,
    private router : Router,
      private preloadService: WorkitemPreloadService,
      private storage: StorageService,
      private jwtHelper: JwtHelperService
  ) {
      this.serverUrl = this.apiService.getCurentServer(ServerTypes.work);
    this.workOrders$.subscribe((wo) => {
      this.workOrders = wo ?? [];
    });
  }

  public async loadWorkItems() {
    this.setNewWorkOrders(null); // clear curent list, triggers loader
    //await new Promise(r => setTimeout(r, 500));
    this.getCurrentWorkItems().subscribe((workOrders) => {
      this.setNewWorkOrders(workOrders);
      this.cacheNext(0);
    });
  }

  public setCurrentId(woId: string) {
    this.currentWorkItemIdSubject.next(woId);
      const currIndex = this.workOrders.findIndex((wo) => wo.workItemId === woId);
    if(currIndex > -1) this.cacheNext(currIndex +1);
  }

    public preloadItems() {
        return this.http.get<WorkOrder>(`${this.apiService.getCurentServer(ServerTypes.boapi)}v1/workitems/${this.getCurrentUserId()}/preload`);
    }

  public getWorkOrder(workItemId: string) : Observable<WorkOrder> {
    return this.http.get<WorkOrder>(`${this.serverUrl}${this.workorderUrl}${workItemId}`);
  }

  public getLatestWorkorder(type: string): Observable<any> {
    return this.http.get<any>(`${this.serverUrl}${this.workorderUrl}${type}/history`);
  }

  public async reassignWorkorder(command: ReAssignWorkOrderCommand) {
      const targetWorkOrderIndex = this.workOrders.findIndex((wo) => wo.workItemId === command.workItemId);
    const targetWorkOrder = this.workOrders[targetWorkOrderIndex];
    await firstValueFrom(this.http.put(`${this.serverUrl}${this.workorderUrl}${command.workItemId}`, command), { defaultValue: undefined });
    const copiedWorkOrders = [...this.workOrders];
    let newWorkOrder = {
      ...targetWorkOrder,
      ...command
    };
    copiedWorkOrders[targetWorkOrderIndex] = newWorkOrder;
    this.setNewWorkOrders(copiedWorkOrders);
    if(!command.userId || command.userId !== targetWorkOrder.userId) {
      this.nextWorkOrder.next(copiedWorkOrders[targetWorkOrderIndex + 1]);
    }
  }

  public async setWorkOrderAction(woId: string, body: WorkOrderAction) {

    await firstValueFrom(this.saveWorkOrderAction(woId, body), { defaultValue: undefined });
      const targetWorkOrderIndex = this.workOrders.findIndex((wo) => wo.workItemId === woId);

    if(targetWorkOrderIndex > -1) {
      const targetWorkOrder = this.workOrders[targetWorkOrderIndex];
      const copiedWorkOrders = [...this.workOrders];
      let newWorkOrder = { ...targetWorkOrder, action: body.action };
      copiedWorkOrders[targetWorkOrderIndex] = newWorkOrder;
      this.setNewWorkOrders(copiedWorkOrders);
      this.nextWorkOrder.next(copiedWorkOrders[targetWorkOrderIndex + 1]);
      this.cacheNext(targetWorkOrderIndex + 2);
    }  
  }

    public removeWorkItemAction(workItemId: string): Observable<any> {

    return this.http.delete(`${this.serverUrl}${this.workorderUrl}${workItemId}/action`);
  }

  public getWorkOrderAction<T>(woId: string): T {
      return this.workOrders.find((wo) => wo.workItemId === woId)?.action;
    }
    public getCurrentUserId() {
        return this.jwtHelper.decodeToken(this.storage.getLocalStorageItem(CoreStorageEnum.tokenName)).userId;
    }

  private cacheNext(idx: number): void {
    if(this.workOrders?.length > idx) {
      let next = this.workOrders[idx];
      this.preloadService.preloadWorkItem(next);
    }
  }

  private setNewWorkOrders(workOrders: WorkOrder[]) {
    this.workOrdersSubject.next(workOrders);
  }

   private getCurrentWorkItems(): Observable<WorkOrder[]> {
    
       return this.http.get<WorkOrder[]>(`${this.serverUrl}${this.workorderUrl}?userid=${this.getCurrentUserId()}`);
  }

  private saveWorkOrderAction(workItemId: string, body: WorkOrderAction): Observable<any> {
    return this.http.post(`${this.serverUrl}${this.workorderUrl}${workItemId}/action`, body);
  }

  public handeClick(workOrder: WorkOrder) {
      if(workOrder.messageType === 'WORK_OBJECTION')
      {
        sessionStorage.setItem('workItemId', workOrder.workItemId);
        sessionStorage.setItem('userId', workOrder.userId);
        this.router.navigate(['kmt', 'objection',  workOrder.id]);
      }
        else
        this.router.navigate(['workorders', 'workitem', workOrder.workItemId]);
    }
}
